import { IContactModel } from '@fi-shared/models/common';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { FiContactInformationTypes } from '@fi-shared/types/fi-contact-information-types';
import { ChangeDetectorRef, Injector } from '@angular/core';
import { FiUserDataService, FiUserSignInType } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { getDefaultCountryId } from '../../core/country-id';
import { UserModel } from '@fi-app/user/user.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { FiContactsService } from '@fi-shared/fi-contacts/fi-contacts.service';
import { ContactService } from '@fi-shared/fi-contacts/contact.service';
import { FiUserHelperService } from '@fi-shared/fi-user-shared/fi-user-helper.service';

export class FiUserSignInSettings {
    signInContact: IContactModel;
    prevSignInContact: IContactModel;
    private prevSignInContactType: FiUserSignInType;
    userSignInType: FiUserSignInType;
    latestEditedContactUid: number;

    private user: UserModel;
    private userDataService: FiUserDataService;
    private cdr: ChangeDetectorRef;
    private fiContactsService: FiContactsService;
    private contactService: ContactService;
    private userHelperService: FiUserHelperService;

    constructor(user: UserModel, injector: Injector) {
        this.userDataService = injector.get(FiUserDataService);
        this.cdr = injector.get(ChangeDetectorRef);
        this.fiContactsService = injector.get(FiContactsService);
        this.contactService = injector.get(ContactService);
        this.userHelperService = injector.get(FiUserHelperService);
        this.user = user;

        this.user.EmailAddressContacts = this.getContactsSetUids(this.user.EmailAddressContacts);
        this.user.PhoneNumberContacts = this.getContactsSetUids(this.user.PhoneNumberContacts);

        this.initializeUserSignInSettings();
    }

    private initializeUserSignInSettings(): void {
        this.setInitialSignInType();
        this.setInitialSignInEmail();
    }

    private setInitialSignInType() {
        this.signInContact = this.getNewEmailContact();

        if (this.allowCustomSignIn()) {
            if (this.user.IsSystemObject) {
                this.userSignInType = FiUserSignInType.Custom;
            } else {
                this.userSignInType =
                    !this.isNewUser() && isDefined(this.getExistingSignInContact(this.user.PhoneNumberContacts))
                        ? FiUserSignInType.PhoneNumber
                        : FiUserSignInType.Email;
            }
            this.signInContact.ContactValue = this.user.LoginName;
            return;
        }

        if (this.isNewUser()) {
            this.userSignInType = FiUserSignInType.Email;
            this.signInContact = cloneDeep(this.user.EmailAddressContacts[0]);
            return;
        }

        const emailSignInContact: IContactModel = cloneDeep(
            this.getExistingSignInContact(this.user.EmailAddressContacts),
        );
        if (isDefined(emailSignInContact)) {
            this.signInContact = emailSignInContact;
            this.userSignInType = FiUserSignInType.Email;
            this.updateUserLoginNameByContact(this.signInContact);
            return;
        }

        const phoneNumberSignInContact: IContactModel = cloneDeep(
            this.getExistingSignInContact(this.user.PhoneNumberContacts),
        );
        if (isDefined(phoneNumberSignInContact)) {
            this.signInContact = phoneNumberSignInContact;
            this.userSignInType = FiUserSignInType.PhoneNumber;
            this.updateUserLoginNameByContact(this.signInContact);
            return;
        }

        this.userSignInType = FiUserSignInType.Email;
        this.signInContact.ContactValue = this.user.LoginName;
        this.user.EmailAddressContacts = this.concatContactAndSort(this.user.EmailAddressContacts);
    }

    setCurrentSignInContact(newSignInContact: IContactModel) {
        this.resetSignInOnEmailContacts();
        this.resetSignInOnPhoneNumberContacts();
        newSignInContact.IsSignIn = true;

        this.userSignInType = this.user.EmailAddressContacts.find((userEmail: IContactModel) => {
            return userEmail.Uid === newSignInContact.Uid;
        })
            ? FiUserSignInType.Email
            : FiUserSignInType.PhoneNumber;

        this.prevSignInContact = undefined;
        this.prevSignInContactType = undefined;
        this.onLoginTypeChange(true);
        this._triggerSignInTypeChanged.next(this.userSignInType);
        this._triggerUpdateSignInInputValidity.next(this.userSignInType);
    }

    private _triggerSignInTypeChanged: BehaviorSubject<FiUserSignInType> = new BehaviorSubject<FiUserSignInType>(
        undefined,
    );
    get signInTypeChanged$(): Observable<FiUserSignInType> {
        return this._triggerSignInTypeChanged.asObservable();
    }

    private _triggerUpdateSignInInputValidity: BehaviorSubject<FiUserSignInType> =
        new BehaviorSubject<FiUserSignInType>(undefined);
    get updateSignInInputValidity$(): Observable<FiUserSignInType> {
        return this._triggerUpdateSignInInputValidity.asObservable();
    }

    onPhoneNumberContactChange(contact?: IContactModel) {
        this.prevSignInContact = undefined;
        this.prevSignInContactType = undefined;

        const { ContactValue, ExtendedValue, CountryId } = contact ?? this.signInContact;
        const contactNewValue: IContactModel = { ContactValue, ExtendedValue, CountryId };
        this.resetSignInOnEmailContacts();
        if (isDefined(contact)) {
            this.onSignInContactUpdate(contact);
        } else {
            this.user.PhoneNumberContacts = this.getUpdatedUserContactsWithSignInData(
                this.user.PhoneNumberContacts,
                contactNewValue,
            );
        }
        this.cdr.markForCheck();
    }

    onLoginNameChanged(contact?: IContactModel) {
        if (this.allowCustomSignIn()) {
            // user edited by system user, allow any login, just update LoginName
            this.updateUserLoginNameByContact(this.signInContact);
            return;
        }

        this.prevSignInContact = undefined;
        this.prevSignInContactType = undefined;

        const { ContactValue } = contact ?? this.signInContact;
        this.resetSignInOnPhoneNumberContacts();

        if (isDefined(contact)) {
            this.onSignInContactUpdate(contact);
        } else {
            this.user.EmailAddressContacts = this.getUpdatedUserContactsWithSignInData(this.user.EmailAddressContacts, {
                ContactValue,
            });
        }

        this.cdr.markForCheck();
    }

    private onSignInContactUpdate(contact: IContactModel) {
        contact.IsSignIn = true;
        this.signInContact = cloneDeep(this.getNewSignInContactFromExisting(contact, true));
        this.updateUserLoginNameByContact(contact);
        this._triggerUpdateSignInInputValidity.next(this.userSignInType);
    }

    updateUserLoginNameByContact(contact: IContactModel) {
        this.user.LoginName = this.fiContactsService.getFullContactValue(contact);
    }

    private getUpdatedUserContactsWithSignInData(
        contacts: IContactModel[],
        contactNewValue: IContactModel,
    ): IContactModel[] {
        this.signInContact.IsSignIn = true;
        let existingSignContact: IContactModel = this.getExistingSignInContact(contacts);
        if (isDefined(existingSignContact)) {
            if (this.isNewUser()) existingSignContact.IsSignIn = true;

            this.signInContact = cloneDeep(this.getNewSignInContactFromExisting(existingSignContact));
            this.signInContact = { ...this.signInContact, ...contactNewValue };

            contacts = !!existingSignContact.Id
                ? this.concatContactAndSort(contacts)
                : contacts.map((contact: IContactModel) => {
                      return contact.Uid === existingSignContact.Uid
                          ? { ...existingSignContact, ...contactNewValue }
                          : contact;
                  });
        } else {
            contacts = this.concatContactAndSort(contacts);
        }

        this.updateUserLoginNameByContact(this.signInContact);
        this._triggerUpdateSignInInputValidity.next(this.userSignInType);
        return contacts;
    }

    private getNewSignInContactFromExisting(basedContact: IContactModel, skipSignInReset?: boolean): IContactModel {
        // we need to create a new contact if existingSignInContact is saved before.
        //  https://chainformation.atlassian.net/browse/FI-5896 Scenario 1;
        if (basedContact.Id) {
            const newSignInContact = cloneDeep(basedContact);
            if (!skipSignInReset) {
                basedContact.IsSignIn = false;
                newSignInContact.Uid = this.fiContactsService.getContactUid();
            } else {
                newSignInContact.Uid = basedContact.Uid;
            }
            newSignInContact.Id = undefined;
            newSignInContact.IsDefault = false;
            newSignInContact.IsSignIn = true;
            return newSignInContact;
        }

        return basedContact;
    }

    doContactChecks(): boolean {
        return !this.userHelperService.isPKUser(this.user);
    }

    allowCustomSignIn(): boolean {
        return this.userDataService.isUserSystemObject() && !this.userHelperService.isPKUser(this.user);
    }

    onLoginTypeChange(preventSettingPrevSignInContact?: boolean) {
        let newSignInContact: IContactModel;

        if (this.isEmailSignInType()) {
            newSignInContact = this.getSignInContactOnLoginTypeChange(
                this.user.EmailAddressContacts,
                this.getNewEmailContact(),
            );
            this.resetSignInOnPhoneNumberContacts();
        } else if (this.isPhoneNumberSignInType()) {
            newSignInContact = this.getSignInContactOnLoginTypeChange(
                this.user.PhoneNumberContacts,
                this.getNewPhoneNumberContact(),
            );
            this.resetSignInOnEmailContacts();
        }

        if (!!this.signInContact.ContactValue?.length && !preventSettingPrevSignInContact)
            this.prevSignInContact = cloneDeep(this.signInContact);

        this.prevSignInContactType = this.contactService.isPhoneNumberType(this.signInContact.ContactType)
            ? FiUserSignInType.PhoneNumber
            : FiUserSignInType.Email;
        this.signInContact = cloneDeep(this.getNewSignInContactFromExisting(newSignInContact, true));

        this.cdr.markForCheck();
        this.updateUserLoginNameByContact(this.signInContact);
        this._triggerSignInTypeChanged.next(this.userSignInType);
        this._triggerUpdateSignInInputValidity.next(this.userSignInType);
    }

    private getSignInContactOnLoginTypeChange(contacts: IContactModel[], newContact: IContactModel): IContactModel {
        if (isDefined(this.prevSignInContact)) {
            const existingContactSignIn: IContactModel = contacts.find((existingContact: IContactModel) => {
                return existingContact.Uid === this.prevSignInContact.Uid;
            });

            if (isDefined(existingContactSignIn)) {
                existingContactSignIn.IsSignIn = true;
                return existingContactSignIn;
            }
        }

        let signInContact: IContactModel = this.getExistingSignInContact(contacts);
        if (isDefined(signInContact)) return signInContact;

        signInContact = this.isSingleContactEmpty(contacts) ? contacts[0] : newContact;
        signInContact.IsSignIn = true;
        return signInContact;
    }

    isPhoneNumberSignInType(): boolean {
        return this.userSignInType === FiUserSignInType.PhoneNumber;
    }

    isEmailSignInType(): boolean {
        return this.userSignInType === FiUserSignInType.Email;
    }

    isCustomSignInType(): boolean {
        return this.userSignInType === FiUserSignInType.Custom;
    }

    isPrevSignInContactCurrentType(isPhoneNumberContactType: boolean): boolean {
        if (isDefined(this.prevSignInContactType)) return false;

        return (
            (this.prevSignInContactType === FiUserSignInType.PhoneNumber && isPhoneNumberContactType) ||
            (this.prevSignInContactType === FiUserSignInType.Email && !isPhoneNumberContactType)
        );
    }

    getNewPhoneNumberContact(): IContactModel {
        const defaultContact: IContactModel = this.user.PhoneNumberContacts.find((contact: IContactModel) => {
            return contact.IsDefault;
        });

        const defaultCountryId: number =
            defaultContact?.CountryId ?? getDefaultCountryId(this.userDataService.applicationLanguage);

        return <IContactModel>{
            CountryId: defaultCountryId,
            IsSignIn: true,
            ContactType: FiContactInformationTypes.WorkPhoneNumber,
            ContactValue: '',
            Uid: this.fiContactsService.getContactUid(),
        };
    }

    private setInitialSignInEmail() {
        if (!this.isNewUser() || !this.user.EmailAddressContacts?.length) return;

        const existingPrimaryEmailAddress: IContactModel = this.user.EmailAddressContacts[0];
        existingPrimaryEmailAddress.IsSignIn = true;
    }

    private concatContactAndSort(contacts: IContactModel[]): IContactModel[] {
        this.signInContact.IsSignIn = true;
        return contacts.concat(this.signInContact).sort(comparator((contact) => (contact.IsSignIn ? 0 : 1)));
    }

    private getExistingSignInContact(contacts: IContactModel[]): IContactModel {
        const signInContact: IContactModel = contacts?.find((contact: IContactModel) => contact.IsSignIn);
        return this.isSignInContactNotSetForNewUser(signInContact, contacts) ? contacts[0] : signInContact;
    }

    private isSignInContactNotSetForNewUser(signInContact: IContactModel, contacts: IContactModel[]): boolean {
        return this.isNewUser() && !isDefined(signInContact) && this.isSingleContactEmpty(contacts);
    }

    private isSingleContactEmpty(contacts: IContactModel[]): boolean {
        return contacts.length === 1 && contacts[0].ContactValue === '';
    }

    private getNewEmailContact(): IContactModel {
        return {
            ContactType: FiContactInformationTypes.WorkEmailAddress,
            ContactValue: '',
            IsSignIn: true,
            Uid: this.fiContactsService.getContactUid(),
        };
    }

    private isNewUser(): boolean {
        return !this.user.Id;
    }

    private resetSignInOnPhoneNumberContacts() {
        this.user.PhoneNumberContacts.forEach((contact: IContactModel) => (contact.IsSignIn = false));
    }

    private resetSignInOnEmailContacts() {
        this.user.EmailAddressContacts.forEach((contact: IContactModel) => (contact.IsSignIn = false));
    }

    private getContactsSetUids(contacts: IContactModel[]): IContactModel[] {
        return contacts.map((contact: IContactModel) => {
            return contact.hasOwnProperty('Uid')
                ? contact
                : { ...contact, Uid: this.fiContactsService.getContactUid() };
        });
    }
}
